/**
 * Token Manager Service
 *
 * Manages JWT access and refresh tokens in memory for security.
 * - Access tokens stored in memory (not localStorage to prevent XSS attacks)
 * - Refresh tokens can be stored in memory or secure httpOnly cookie (backend decision)
 * - Automatic token refresh before expiry
 * - Token lifecycle management
 */

/**
 * Simple JWT decoder (does not validate signature, only parses payload)
 * @param {string} token - JWT token
 * @returns {Object|null} - Decoded payload or null if invalid
 */
function decodeJWT(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

class TokenManager {
  constructor() {
    this.accessToken = null;
    this.tokenRefreshTimer = null;
    this.tokenRefreshCallback = null;
  }

  /**
   * Store access token (refresh token is stored in httpOnly cookie by backend)
   * @param {string} accessToken - JWT access token
   */
  setTokens(accessToken) {
    this.accessToken = accessToken;

    // Schedule automatic refresh before token expires
    this.scheduleTokenRefresh();
  }

  /**
   * Get the current access token
   * @returns {string|null}
   */
  getAccessToken() {
    return this.accessToken;
  }

  /**
   * Get the current refresh token
   * NOTE: Refresh token is stored in httpOnly cookie, not accessible from JavaScript
   * @returns {null}
   */
  getRefreshToken() {
    return null; // Always null - refresh token is in httpOnly cookie
  }

  /**
   * Check if user has valid tokens
   * @returns {boolean}
   */
  isAuthenticated() {
    if (!this.accessToken) {
      return false;
    }

    try {
      const decoded = decodeJWT(this.accessToken);
      if (!decoded) {
        return false;
      }

      const currentTime = Date.now() / 1000;

      // Check if token is expired
      return decoded.exp > currentTime;
    } catch (error) {
      console.error('Error decoding token:', error);
      return false;
    }
  }

  /**
   * Get token expiry time
   * @returns {number|null} - Timestamp in seconds, or null if no token
   */
  getTokenExpiry() {
    if (!this.accessToken) {
      return null;
    }

    try {
      const decoded = decodeJWT(this.accessToken);
      return decoded ? decoded.exp : null;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Get time until token expires (in milliseconds)
   * @returns {number|null}
   */
  getTimeUntilExpiry() {
    const expiry = this.getTokenExpiry();
    if (!expiry) {
      return null;
    }

    const currentTime = Date.now() / 1000;
    const timeUntilExpiry = (expiry - currentTime) * 1000;

    return Math.max(0, timeUntilExpiry);
  }

  /**
   * Schedule automatic token refresh before expiry
   * Refreshes 1 minute before token expires
   */
  scheduleTokenRefresh() {
    // Clear existing timer
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }

    const timeUntilExpiry = this.getTimeUntilExpiry();
    if (!timeUntilExpiry) {
      return;
    }

    // Refresh 1 minute (60 seconds) before expiry
    const refreshTime = timeUntilExpiry - (60 * 1000);

    // Don't schedule if token expires in less than 1 minute
    if (refreshTime <= 0) {
      console.warn('Token expires soon, manual refresh needed');
      return;
    }

    this.tokenRefreshTimer = setTimeout(() => {
      if (this.tokenRefreshCallback) {
        this.tokenRefreshCallback()
          .then(({ accessToken }) => {
            this.setTokens(accessToken);
          })
          .catch((error) => {
            console.error('Auto token refresh failed:', error);
            this.clearTokens();
          });
      }
    }, refreshTime);
  }

  /**
   * Set callback function for automatic token refresh
   * @param {Function} callback - Function that returns Promise with new tokens
   */
  setRefreshCallback(callback) {
    this.tokenRefreshCallback = callback;
  }

  /**
   * Clear all tokens and cancel refresh timer
   * Note: This only clears the in-memory access token.
   * The refresh token cookie is cleared by calling the logout endpoint.
   */
  clearTokens() {
    this.accessToken = null;

    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  /**
   * Get decoded token payload (without verification)
   * @returns {Object|null}
   */
  getDecodedToken() {
    if (!this.accessToken) {
      return null;
    }

    try {
      return decodeJWT(this.accessToken);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Get user info from token
   * @returns {Object|null}
   */
  getUserFromToken() {
    const decoded = this.getDecodedToken();
    if (!decoded) {
      return null;
    }

    // Extract user info from token claims
    return {
      username: decoded.username || decoded.sub,
      roles: decoded.roles || [],
      ...decoded
    };
  }

  /**
   * Check if current token is an impersonation token
   * @returns {boolean}
   */
  isImpersonated() {
    const decoded = this.getDecodedToken();
    return decoded && decoded.isImpersonated === true;
  }

  /**
   * Get original user info from impersonation token
   * @returns {Object|null} - Original user info (username, first_name, last_name) or null if not impersonating
   */
  getOriginalUser() {
    const decoded = this.getDecodedToken();
    if (!decoded || !decoded.isImpersonated || !decoded.originalUser) {
      return null;
    }
    return decoded.originalUser;
  }

  /**
   * Get impersonated user info from token
   * @returns {Object|null} - Current user info from token or null if no token
   */
  getImpersonatedUser() {
    const decoded = this.getDecodedToken();
    if (!decoded) {
      return null;
    }

    // Return current user info from token (this is the impersonated user)
    return {
      username: decoded.username || decoded.sub,
      roles: decoded.roles || [],
      name: decoded.name,
      ...decoded
    };
  }
}

// Export singleton instance
export default new TokenManager();
